import { Copy, Plus, Wallet } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import {
  MAX_FORM_LINK_APPLICATIONS,
  postingChannelName,
  formatMoney,
  type FormLinkPlace,
  type JobDetail,
  type JobPostingsView,
  type PostingView,
  type PublishableChannel,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Card, MetricStrip } from '../../../../components/Card';
import { Checkbox } from '../../../../components/Checkbox';
import { DatePicker } from '../../../../components/DatePicker';
import { Field } from '../../../../components/Field';
import { Input } from '../../../../components/Input';
import { Modal } from '../../../../components/Modal';
import { Popover, usePopover } from '../../../../components/Popover';
import { Segmented } from '../../../../components/Segmented';
import sheet from '../../../../components/SheetTable.module.css';
import { SkeletonLines } from '../../../../components/Skeleton';
import { Switch } from '../../../../components/Switch';
import { EmptyState, ServerErrorState } from '../../../../components/system-states/SystemState';
import { ToneChip } from '../../../../components/Tone';
import { errorMessage } from '../../../../lib/api-client';
import { isoToDhaka } from '../../../../lib/dhaka-time';
import { EMPTY, formatIsoDate, formatNumber, formatTime } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { useChangePosting, useJobPostings, type PostingChange } from '../../api/publishing-api';
import { addDays, bdToday, closingInstant, daysBetween, formLinkStatus, type FormLinkStatus } from './form-link-status';
import styles from './hub.module.css';

/**
 * POST-01, as "Form links" (DECISIONS-LOG 2026-10-27): a shareable link to this job's
 * application form for each place it's posted, each opening and closing on its own dates
 * and counting who applied through it. No job board has an API here (D8): the recruiter
 * pastes the link as the advert's apply link — or behind the website's Apply button — so
 * every applicant arrives already attributed.
 */

const FORM_WIDTH = 360;
const MINOR_PER_MAJOR = 100;
const OTHER = 'other';
const SHORT_LINK_CHARS = 26;

/** The drawing's duration buttons. `none` stays open until the job closes; `pick` shows a date. */
const DURATIONS = [
  { value: 'd3', label: '3 days', days: 3 },
  { value: 'd7', label: '1 week', days: 7 },
  { value: 'd14', label: '2 weeks', days: 14 },
  { value: 'd30', label: '1 month', days: 30 },
  { value: 'none', label: 'Until the job closes', days: null },
  { value: 'pick', label: 'Pick a date', days: null },
] as const;
type DurationChoice = (typeof DURATIONS)[number]['value'];
const DEFAULT_DURATION: DurationChoice = 'd14';
const DEFAULT_LIMIT = '200';

type Run = (change: PostingChange) => Promise<JobPostingsView | null>;

export function PostingsTab({ detail }: { readonly detail: JobDetail }): ReactNode {
  const panel = useJobPostings(detail.id);
  if (panel.isPending) return <SkeletonLines count={6} />;
  if (panel.error) return <ServerErrorState onRetry={() => void panel.refetch()} />;
  return <Panel detail={detail} panel={panel.data} />;
}

function Panel({ detail, panel }: { readonly detail: JobDetail; readonly panel: JobPostingsView }): ReactNode {
  const change = useChangePosting(detail.id);
  const show = useToasts((state) => state.show);
  // Feedback stays on the link being changed (CLAUDE.md §5a rule 4).
  const [pendingId, setPendingId] = useState<string | null>(null);
  const today = bdToday();
  const canCreate = panel.canManage && !panel.publishBlocked;
  const [isNewOpen, setIsNewOpen] = useState(false);
  const newLinkButton = <NewLinkButton onOpen={() => setIsNewOpen(true)} />;

  async function run(input: PostingChange): Promise<JobPostingsView | null> {
    setPendingId('postingId' in input ? input.postingId : 'new');
    try {
      return await change.mutateAsync(input);
    } catch (cause) {
      show({ tone: 'danger', message: "That didn't save", detail: errorMessage(cause) });
      return null;
    } finally {
      setPendingId(null);
    }
  }

  const rows = panel.postings.map((link) => ({ link, status: formLinkStatus(link, today) }));
  const count = (...phases: readonly FormLinkStatus['phase'][]): number => rows.filter((row) => phases.includes(row.status.phase)).length;

  return (
    <div className={styles.postings}>
      {isNewOpen && canCreate && <NewLinkDialog panel={panel} today={today} onCreate={run} onClose={() => setIsNewOpen(false)} />}
      {rows.length > 0 && (
        <MetricStrip
          metrics={[
            { label: 'Open now', value: formatNumber(count('open', 'closing')) },
            { label: 'Not open yet', value: formatNumber(count('scheduled')) },
            { label: 'Applications', value: formatNumber(rows.reduce((sum, row) => sum + row.link.applicants, 0)) },
            { label: 'Closing soon', value: formatNumber(count('closing')) },
          ]}
        />
      )}
      <Card
        title="Form links"
        meta="Optional. A link to this job's application form for each place you post it. Each one closes on its own date and counts who applied through it."
        action={canCreate ? newLinkButton : undefined}
      >
        {panel.publishBlocked && panel.canManage && <p className={styles.blocked}>{panel.publishBlocked}</p>}
        {rows.length === 0 ? (
          <EmptyState
            title="No form links yet"
            body="Make one for each place you post this job, such as your website, LinkedIn or a job fair. Each closes on its own date and counts who applied through it."
            action={canCreate ? newLinkButton : null}
            isInset
          />
        ) : (
          <div className={styles.linkTable}>
            <table className={sheet.sheet}>
              <thead>
                <tr>
                  <th scope="col">Link</th>
                  <th scope="col">Status</th>
                  <th scope="col">Open window</th>
                  <th scope="col" className={styles.numCol}>
                    Applications
                  </th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ link, status }) => (
                  <LinkRow
                    key={link.id}
                    link={link}
                    status={status}
                    canManage={panel.canManage}
                    canReopen={panel.publishBlocked === null}
                    isPending={pendingId === link.id}
                    onChange={run}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {panel.directApplicants > 0 && (
          <p className={styles.muted}>
            {formatNumber(panel.directApplicants)} {panel.directApplicants === 1 ? 'applicant' : 'applicants'} came without a
            form link and {panel.directApplicants === 1 ? 'is' : 'are'} counted as “Direct / unknown”.
          </p>
        )}
      </Card>

      <Card title="Employee referrals" meta="Staff get their own link from “Refer someone”; whoever applies through it is marked as referred.">
        {panel.referralLinks.length === 0 ? (
          <p className={styles.muted}>No one has shared a referral link for this job yet.</p>
        ) : (
          <ul className={styles.referrals}>
            {panel.referralLinks.map((link) => (
              <li key={link.id} className={styles.referral}>
                <span>{link.referrerName ?? EMPTY}</span>
                <span>
                  {formatNumber(link.applicants)} {link.applicants === 1 ? 'applicant' : 'applicants'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function LinkRow({
  link,
  status,
  canManage,
  canReopen,
  isPending,
  onChange,
}: {
  readonly link: PostingView;
  readonly status: FormLinkStatus;
  readonly canManage: boolean;
  readonly canReopen: boolean;
  readonly isPending: boolean;
  readonly onChange: Run;
}): ReactNode {
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const isOpenOrWaiting = status.phase === 'open' || status.phase === 'closing' || status.phase === 'scheduled';
  const isClosed = status.phase === 'closed';
  const total = spendTotal(link);
  const opensOn = link.goesLiveOn ?? (link.publishedAt ? isoToDhaka(link.publishedAt).date : null);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(link.trackedUrl);
      show({ tone: 'success', message: `${link.channelLabel} link copied`, key: 'posting-copy' });
    } catch {
      show({ tone: 'warning', message: "Couldn't copy automatically", detail: 'Select the link and copy it by hand.' });
    }
  }

  async function closeNow(): Promise<void> {
    const after = await onChange({ kind: 'unpublish', postingId: link.id, version: link.version });
    if (!after) return;
    // Undo beats confirm (CLAUDE.md §5a rule 8): closing just happens, with ten seconds to take it back.
    // The undo reads the version the close left behind, not the one this row drew with.
    showUndo(`${link.channelLabel} link closed`, () => {
      const current = after.postings.find((posting) => posting.id === link.id);
      if (current) void onChange({ kind: 'republish', postingId: link.id, version: current.version });
    });
  }

  async function reopen(): Promise<void> {
    const after = await onChange({ kind: 'republish', postingId: link.id, version: link.version });
    if (!after) return;
    const reopened = after.postings.find((posting) => posting.id === link.id);
    show({
      tone: 'success',
      message: `${link.channelLabel} link reopened`,
      ...(reopened && !reopened.expiresOn ? { detail: 'It has no end date now. Set one under Dates.' } : {}),
    });
  }

  return (
    <tr className={styles.linkRow} data-pending={isPending || undefined} data-closed={isClosed || undefined}>
      <td>
        <div className={styles.linkName}>{link.channelLabel}</div>
        <div className={styles.linkUrl}>
          <span className={styles.linkText} title={link.trackedUrl}>
            {shortLink(link.trackedUrl)}
          </span>
          <Button variant="quiet" size="sm" icon={Copy} aria-label={`Copy the ${link.channelLabel} link`} onClick={() => void copy()} />
        </div>
      </td>
      <td>
        <ToneChip tone={status.tone}>{status.label}</ToneChip>
      </td>
      <td className={styles.windowCell}>
        <span className={styles.dataText}>
          {opensOn ? formatIsoDate(opensOn) : EMPTY} → {link.expiresOn ? formatIsoDate(link.expiresOn) : 'no end date'}
        </span>
        {link.expiresOn && !isClosed && <span className={styles.cellSub}>Closes {formatTime(closingInstant(link.expiresOn))}</span>}
        {link.maxApplications !== null && !isClosed && <span className={styles.cellSub}>Or at {formatNumber(link.maxApplications)} applications</span>}
      </td>
      <td className={styles.numCol}>
        <span className={styles.countText}>
          {formatNumber(link.applicants)}
          {link.maxApplications !== null && <span className={styles.cellSub}> of {formatNumber(link.maxApplications)}</span>}
        </span>
        <span className={styles.cellSub}>
          {formatNumber(link.views)} {link.views === 1 ? 'view' : 'views'}
        </span>
        {total && <span className={styles.cellSub}>{total} spent</span>}
      </td>
      <td>
        {canManage && (
          <div className={styles.rowActions}>
            {isOpenOrWaiting && <DatesButton link={link} onChange={onChange} />}
            {status.phase !== 'paused' && <SpendButton link={link} onChange={onChange} />}
            {isOpenOrWaiting && (
              <Button variant="ghost" size="sm" disabled={isPending} onClick={() => void closeNow()}>
                Close now
              </Button>
            )}
            {isClosed && canReopen && (
              <Button variant="outline" size="sm" disabled={isPending} onClick={() => void reopen()}>
                Reopen
              </Button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

interface PlaceOption {
  readonly key: string;
  readonly label: string;
  readonly channel: PublishableChannel;
  readonly sourceKey: string | null;
}

/** The two "New form link" buttons (card header, empty state) only open the one dialog the panel holds. */
function NewLinkButton({ onOpen }: { readonly onOpen: () => void }): ReactNode {
  return (
    <Button variant="solid" icon={Plus} onClick={onOpen} aria-haspopup="dialog">
      New form link
    </Button>
  );
}

/**
 * F6.01 / F6.02 — tick the places, choose how long, get a link each (DECISIONS-LOG 2026-10-27).
 * A centred dialog, opened from either button, because the owner asked for it to appear in the same
 * place every time (DECISIONS-LOG 2026-10-28). It is mounted only while open, so each opening is a fresh form.
 */
function NewLinkDialog({
  panel,
  today,
  onCreate,
  onClose,
}: {
  readonly panel: JobPostingsView;
  readonly today: string;
  readonly onCreate: Run;
  readonly onClose: () => void;
}): ReactNode {
  const show = useToasts((state) => state.show);
  const formId = useId();
  // S31 (ADMIN-08): the HR Lead's own job boards sit between the built-ins and "Other".
  const places: readonly PlaceOption[] = [
    ...panel.availableChannels
      .filter((channel) => channel !== OTHER)
      .map((channel) => ({ key: channel, label: postingChannelName(channel, null), channel, sourceKey: null })),
    ...panel.availableSources.map((source) => ({ key: source.key, label: source.label, channel: 'other' as const, sourceKey: source.key })),
  ];
  const canAddOther = panel.availableChannels.includes(OTHER);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [isOtherPicked, setIsOtherPicked] = useState(false);
  const [otherName, setOtherName] = useState('');
  const [duration, setDuration] = useState<DurationChoice>(DEFAULT_DURATION);
  const [opensOn, setOpensOn] = useState('');
  const [pickedEnd, setPickedEnd] = useState('');
  const [hasLimit, setHasLimit] = useState(false);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [isSaving, setIsSaving] = useState(false);

  const start = opensOn || today;
  const days = DURATIONS.find((option) => option.value === duration)?.days ?? null;
  const expiresOn = days !== null ? addDays(start, days) : duration === 'pick' ? pickedEnd : '';
  const placeCount = picked.size + (isOtherPicked ? 1 : 0);
  const limitValue = Number(limit);
  const isLimitValid = !hasLimit || (/^\d+$/.test(limit) && limitValue >= 1 && limitValue <= MAX_FORM_LINK_APPLICATIONS);
  const endProblem = duration === 'pick' && pickedEnd !== '' && pickedEnd < start ? 'Close it on or after the day it opens.' : undefined;
  const isReady =
    placeCount > 0 &&
    (!isOtherPicked || otherName.trim().length >= 2) &&
    (duration !== 'pick' || (pickedEnd !== '' && !endProblem)) &&
    isLimitValid &&
    !isSaving;

  function togglePlace(key: string, isOn: boolean): void {
    const next = new Set(picked);
    if (isOn) next.add(key);
    else next.delete(key);
    setPicked(next);
  }

  async function submit(): Promise<void> {
    if (!isReady) return;
    const chosen = places.filter((place) => picked.has(place.key));
    const body: FormLinkPlace[] = chosen.map((place) => ({ channel: place.channel, ...(place.sourceKey ? { sourceKey: place.sourceKey } : {}) }));
    if (isOtherPicked) body.push({ channel: 'other', channelName: otherName.trim() });
    setIsSaving(true);
    const after = await onCreate({
      kind: 'publish',
      body: {
        places: body,
        ...(opensOn ? { goesLiveOn: opensOn } : {}),
        ...(expiresOn ? { expiresOn } : {}),
        ...(hasLimit ? { maxApplications: limitValue } : {}),
      },
    });
    setIsSaving(false);
    if (!after) return;
    onClose();
    const onlyLabel = body.length === 1 ? (isOtherPicked ? otherName.trim() : chosen[0]?.label) : null;
    if (!onlyLabel) {
      show({ tone: 'success', message: `${body.length} form links made`, detail: 'Copy each one from its row.' });
      return;
    }
    // One link: made and copied in the same click, as drawn.
    const made = after.postings.find((posting) => posting.channelLabel === onlyLabel);
    try {
      if (made) await navigator.clipboard.writeText(made.trackedUrl);
      show({ tone: 'success', message: made ? `${onlyLabel} link made and copied` : `${onlyLabel} link made` });
    } catch {
      show({ tone: 'success', message: `${onlyLabel} link made`, detail: 'Copy it from its row.' });
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="New form link"
      description="Tick one place or several. Each gets its own link."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="solid" type="submit" form={formId} disabled={!isReady} isPending={isSaving}>
            {placeCount > 1 ? `Make ${placeCount} links` : 'Make link'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className={styles.popoverForm}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <fieldset className={styles.placeSet}>
          <legend className={styles.placeLegend}>Where will you post it?</legend>
          <div className={styles.placeGrid}>
            {places.map((place) => (
              <Checkbox key={place.key} checked={picked.has(place.key)} label={place.label} onChange={(isOn) => togglePlace(place.key, isOn)} />
            ))}
            {canAddOther && <Checkbox checked={isOtherPicked} label="Somewhere else" onChange={setIsOtherPicked} />}
          </div>
          {places.length === 0 && !canAddOther && <p className={styles.placeHint}>Every place already has a link. Reopen one instead.</p>}
        </fieldset>
        {isOtherPicked && (
          <Field label="Name the place" isRequired hint="For example NSU job fair or Chakri.com">
            {({ inputId, describedBy }) => (
              <Input id={inputId} aria-describedby={describedBy} value={otherName} maxLength={60} onChange={(event) => setOtherName(event.target.value)} />
            )}
          </Field>
        )}
        <div className={styles.placeSet}>
          <span className={styles.placeLegend}>Keep it open for</span>
          <Segmented label="Keep it open for" value={duration} options={DURATIONS.map(({ value, label }) => ({ value, label }))} onChange={setDuration} />
        </div>
        <div className={styles.row}>
          <Field label="Opens" isOptional hint="Leave empty to open now">
            {({ inputId, describedBy }) => <DatePicker id={inputId} label="Opens" describedBy={describedBy} value={opensOn} onChange={setOpensOn} />}
          </Field>
          {duration === 'pick' && (
            <Field label="Closes" isRequired error={endProblem}>
              {({ inputId, describedBy }) => <DatePicker id={inputId} label="Closes" describedBy={describedBy} value={pickedEnd} onChange={setPickedEnd} />}
            </Field>
          )}
        </div>
        <Switch checked={hasLimit} onChange={setHasLimit} label="Also close after a number of applications" />
        {hasLimit && (
          <Field label="Most applications" isRequired error={isLimitValid ? undefined : 'Enter a whole number, 1 or more.'}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} inputMode="numeric" isNumeric value={limit} onChange={(event) => setLimit(event.target.value)} />
            )}
          </Field>
        )}
        <p className={styles.readout} aria-live="polite">
          {windowSentence(opensOn, expiresOn, start, today)}
        </p>
      </form>
    </Modal>
  );
}

/** "Open now until 09-11-2026, 11:59 pm GMT+6. That's 14 days." */
function windowSentence(opensOn: string, expiresOn: string, start: string, today: string): string {
  const opens = opensOn && opensOn > today ? `Opens ${formatIsoDate(opensOn)}` : 'Open now';
  if (!expiresOn) return `${opens}, and stays open until the job closes.`;
  const length = daysBetween(start, expiresOn);
  return `${opens} until ${formatIsoDate(expiresOn)}, ${formatTime(closingInstant(expiresOn))}. That's ${length} ${length === 1 ? 'day' : 'days'}.`;
}

function DatesButton({ link, onChange }: { readonly link: PostingView; readonly onChange: Run }): ReactNode {
  const show = useToasts((state) => state.show);
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [goesLiveOn, setGoesLiveOn] = useState(link.goesLiveOn ?? '');
  const [expiresOn, setExpiresOn] = useState(link.expiresOn ?? '');
  const [hasLimit, setHasLimit] = useState(link.maxApplications !== null);
  const [limit, setLimit] = useState(String(link.maxApplications ?? DEFAULT_LIMIT));
  const isScheduled = link.status === 'draft';
  const limitValue = Number(limit);
  const isLimitValid = !hasLimit || (/^\d+$/.test(limit) && limitValue >= 1 && limitValue <= MAX_FORM_LINK_APPLICATIONS);

  async function submit(): Promise<void> {
    if (!isLimitValid) return;
    const after = await onChange({
      kind: 'update',
      postingId: link.id,
      body: {
        version: link.version,
        ...(isScheduled ? { goesLiveOn: goesLiveOn || null } : {}),
        expiresOn: expiresOn || null,
        maxApplications: hasLimit ? limitValue : null,
      },
    });
    if (!after) return;
    show({ tone: 'success', message: `${link.channelLabel} link dates saved` });
    close();
  }

  return (
    <>
      <Button ref={triggerRef} variant="ghost" size="sm" onClick={toggle} aria-expanded={isOpen}>
        Dates
      </Button>
      {isOpen && (
        <Popover anchor={anchor} onClose={close} width={FORM_WIDTH} label={`${link.channelLabel} link dates`}>
          <form
            className={styles.popoverForm}
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <div className={styles.row}>
              {isScheduled && (
                <Field label="Opens" isOptional>
                  {({ inputId }) => <DatePicker id={inputId} label="Opens" value={goesLiveOn} onChange={setGoesLiveOn} />}
                </Field>
              )}
              <Field label="Closes" isOptional hint="You're reminded three days before">
                {({ inputId, describedBy }) => <DatePicker id={inputId} label="Closes" describedBy={describedBy} value={expiresOn} onChange={setExpiresOn} />}
              </Field>
            </div>
            <Switch checked={hasLimit} onChange={setHasLimit} label="Also close after a number of applications" />
            {hasLimit && (
              <Field label="Most applications" isRequired error={isLimitValid ? undefined : 'Enter a whole number, 1 or more.'}>
                {({ inputId, describedBy, isInvalid }) => (
                  <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} inputMode="numeric" isNumeric value={limit} onChange={(event) => setLimit(event.target.value)} />
                )}
              </Field>
            )}
            <div className={styles.reasonActions}>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button variant="solid" type="submit" disabled={!isLimitValid}>
                Save
              </Button>
            </div>
          </form>
        </Popover>
      )}
    </>
  );
}

/** F6.04. Typed in whole taka; stored as minor units (non-negotiable #7). */
function SpendButton({ link, onChange }: { readonly link: PostingView; readonly onChange: Run }): ReactNode {
  const show = useToasts((state) => state.show);
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const whole = amount.replace(/[,\s]/g, '');
  const isAmountValid = /^\d{1,9}$/.test(whole) && Number(whole) > 0;
  const isReady = isAmountValid && description.trim().length >= 2;

  async function submit(): Promise<void> {
    if (!isReady) return;
    const after = await onChange({
      kind: 'spend',
      postingId: link.id,
      body: { amountMinor: String(Number(whole) * MINOR_PER_MAJOR), currency: 'BDT', description: description.trim() },
    });
    if (!after) return;
    show({ tone: 'success', message: `Spend recorded on ${link.channelLabel}` });
    close();
    setAmount('');
    setDescription('');
  }

  return (
    <>
      <Button ref={triggerRef} variant="ghost" size="sm" icon={Wallet} onClick={toggle} aria-expanded={isOpen} aria-label={`Record spend on ${link.channelLabel}`} />
      {isOpen && (
        <Popover anchor={anchor} onClose={close} width={FORM_WIDTH} label={`Spend on ${link.channelLabel}`}>
          <form
            className={styles.popoverForm}
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            {link.spend.length > 0 && (
              <ul className={styles.spend}>
                {link.spend.map((entry) => (
                  <li key={entry.id} className={styles.spendRow}>
                    <span>
                      {formatIsoDate(entry.incurredOn)} · {entry.description}
                    </span>
                    <span className={styles.spendAmount}>{formatMoney({ amountMinor: BigInt(entry.amountMinor), currency: entry.currency })}</span>
                  </li>
                ))}
              </ul>
            )}
            <Field label="Amount" isRequired error={amount !== '' && !isAmountValid ? 'Enter a valid amount.' : undefined}>
              {({ inputId, describedBy, isInvalid }) => (
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  isInvalid={isInvalid}
                  prefix="৳"
                  inputMode="numeric"
                  isNumeric
                  value={amount}
                  autoFocus
                  onChange={(event) => setAmount(event.target.value)}
                />
              )}
            </Field>
            <Field label="What was it for?" isRequired>
              {({ inputId }) => (
                <Input id={inputId} value={description} maxLength={200} placeholder="For example a 30-day hot job" onChange={(event) => setDescription(event.target.value)} />
              )}
            </Field>
            <div className={styles.reasonActions}>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button variant="solid" type="submit" disabled={!isReady}>
                Record
              </Button>
            </div>
          </form>
        </Popover>
      )}
    </>
  );
}

/** The end that tells links apart; every link to one job starts the same. The full link is its tooltip. */
function shortLink(url: string): string {
  const path = url.replace(/^https?:\/\/[^/]+/, '');
  return path.length > SHORT_LINK_CHARS ? `…${path.slice(-SHORT_LINK_CHARS)}` : path;
}

/** One total per currency — amounts in different currencies are never added (#7). */
function spendTotal(link: PostingView): string | null {
  if (link.spend.length === 0) return null;
  const totals = new Map<string, bigint>();
  for (const entry of link.spend) totals.set(entry.currency, (totals.get(entry.currency) ?? 0n) + BigInt(entry.amountMinor));
  return [...totals.entries()]
    .map(([currency, amountMinor]) => formatMoney({ amountMinor, currency: currency === 'GBP' || currency === 'USD' ? currency : 'BDT' }))
    .join(' + ');
}
